import React, { useMemo, useState } from "react";
import { Button, Descriptions, Drawer, Modal, Space, Tag, Typography } from "antd";
import { BankOutlined, CompressOutlined, EditOutlined, ExpandOutlined } from "@ant-design/icons";
import CopyableText from "components/common/CopyableText";
import { formatEnumLabel } from "../constants";
import useFormattedDate from "hooks/useFormatDate";
import { STORAGE_BASE_URL } from "constants";

export default function OrganizationDetailsDrawer({
  open,
  onClose,
  entity,
  loading,
  onEdit,
}) {
  const title = useMemo(() => entity?.name || "Organization", [entity]);
  const { formatDateTime } = useFormattedDate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [fullWidth, setFullWidth] = useState(false);

  const resolveFileUrl = (value) => {
    if (!value) return null;
    const str = String(value);
    if (str.includes("://")) return str; // already a URL (e.g. signed URL)
    return `${STORAGE_BASE_URL}/files/${str}`; // treat as fileId
  };

  const isImageUrl = (url) =>
    /\.(png|jpe?g|webp|gif)(\?|$)/i.test(String(url || ""));

  const isPdfUrl = (url) => /\.pdf(\?|$)/i.test(String(url || ""));

  const openPreview = (url, nextTitle) => {
    setPreviewUrl(url);
    setPreviewTitle(nextTitle || "Preview");
    setPreviewOpen(true);
  };

  const renderFileLink = (value, label = "Open file") => {
    const url = resolveFileUrl(value);
    if (!url) return "-";
    return (
      <Typography.Link href={url} target="_blank" rel="noreferrer">
        {label}
      </Typography.Link>
    );
  };

  const renderImage = (value, label = "Preview") => {
    const url = resolveFileUrl(value);
    if (!url) return "-";
    return (
      <button
        type="button"
        onClick={() => openPreview(url, label)}
        style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
      >
        <img
          src={url}
          alt="uploaded"
          style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8 }}
        />
      </button>
    );
  };

  const renderFileValue = (value, { label, preferImage = false } = {}) => {
    const url = resolveFileUrl(value);
    if (!url) return "-";
    if (preferImage && isImageUrl(url)) return renderImage(url, label || "Preview image");
    if (isImageUrl(url)) return renderImage(url, label || "Preview image");
    if (isPdfUrl(url)) return renderFileLink(url, label || "Open PDF");
    return renderFileLink(url, label || "Open file");
  };

  return (
    <Drawer
      title={
        <Space>
          <span>{title}</span>
        </Space>
      }
      width={fullWidth ? "100vw" : 640}
      styles={{
        body: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        header: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
        footer: { paddingLeft: fullWidth ? 48 : 24, paddingRight: 24 },
      }}
      open={open}
      onClose={onClose}
      destroyOnClose
      placement="right"
      zIndex={2300}
      loading={loading}
      extra={
        <Space>
          <Button
            type="text"
            aria-label={fullWidth ? "Normal drawer width" : "Expand drawer"}
            title={fullWidth ? "Normal width" : "Expand"}
            icon={fullWidth ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setFullWidth((v) => !v)}
          />
          <Button
            type="primary"
            icon={<EditOutlined />}
            disabled={!entity?.id}
            onClick={() => onEdit?.(entity)}
          >
            Edit
          </Button>
        </Space>
      }
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
    >
      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        zIndex={2450}
        centered
      >
        <img src={previewUrl} alt={previewTitle} style={{ width: "100%", borderRadius: 10 }} />
      </Modal>
      <Descriptions bordered size="small" column={1} labelStyle={{ width: 180 }}>
        <Descriptions.Item label="ID">
          {entity?.id ? <CopyableText value={entity.id} showTooltip={false} /> : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Name">{entity?.name || "-"}</Descriptions.Item>
        <Descriptions.Item label="Status">
          {entity?.status ? <Tag>{formatEnumLabel(entity.status)}</Tag> : "-"}
        </Descriptions.Item>
        {entity?.status === "DECLINED" ? (
          <Descriptions.Item label="Decline Reason">
            {entity?.declineReason || "-"}
          </Descriptions.Item>
        ) : null}
        {entity?.status === "SUSPENDED" ? (
          <Descriptions.Item label="Suspension Reason">
            {entity?.suspensionReason || "-"}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label="EIN">{entity?.ein || "-"}</Descriptions.Item>
        <Descriptions.Item label="Location Type">
          {entity?.organizationLocationType?.label || entity?.organizationLocationType?.code || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Description">{entity?.description || "-"}</Descriptions.Item>
        <Descriptions.Item label="Certificate">
          {renderFileValue(entity?.certificate, { label: "Open certificate" })}
        </Descriptions.Item>
        <Descriptions.Item label="Logo">
          {renderFileValue(entity?.logo, { label: "Logo", preferImage: true })}
        </Descriptions.Item>
        <Descriptions.Item label="Profile Photo">
          {renderFileValue(entity?.profilePhoto, { label: "Profile photo", preferImage: true })}
        </Descriptions.Item>
        <Descriptions.Item label="Other Docs">
          {Array.isArray(entity?.otherDocs) && entity.otherDocs.length ? (
            <Space direction="vertical" size={4}>
              {entity.otherDocs.map((id) => (
                <div key={String(id)}>
                  {renderFileValue(id, { label: "Open attachment" })}
                </div>
              ))}
            </Space>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Primary Email">
          {entity?.contactInformation?.primaryEmail || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Primary Phone">
          {entity?.contactInformation?.primaryPhone || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Website">
          {entity?.contactInformation?.websiteUrl ? (
            <Typography.Text ellipsis>{entity.contactInformation.websiteUrl}</Typography.Text>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Address Line 1">
          {entity?.locationInformation?.addressLine1 || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Address Line 2">
          {entity?.locationInformation?.addressLine2 || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="City">{entity?.locationInformation?.city || "-"}</Descriptions.Item>
        <Descriptions.Item label="County">{entity?.locationInformation?.county || "-"}</Descriptions.Item>
        <Descriptions.Item label="State">{entity?.locationInformation?.state || "-"}</Descriptions.Item>
        <Descriptions.Item label="Country">
          {entity?.locationInformation?.country || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Zip Code">
          {entity?.locationInformation?.zipCode || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Latitude">
          {entity?.locationInformation?.latitude ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Longitude">
          {entity?.locationInformation?.longitude ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Place ID">
          {entity?.locationInformation?.placeId || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Created At">
          {entity?.createdAt ? formatDateTime(entity.createdAt) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Updated At">
          {entity?.updatedAt ? formatDateTime(entity.updatedAt) : "-"}
        </Descriptions.Item>
      </Descriptions>
    </Drawer>
  );
}
